/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionRedrawCanvasGrid';

const ActionRedrawCanvasGridManager: ActionManagerFn<
  'ActionRedrawCanvasGrid'
> = (platform: Platform): ActionManager<'ActionRedrawCanvasGrid'> => {
  return {
    ...baseActionManager(platform, 'ActionRedrawCanvasGrid'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Canvas Grid Item ID',
        type: 'ItemID',
        required: true,
        initialFilter: 'CanvasGrid',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'CanvasGrid') {
            return `Item id '${value}' doesn't belong to a Canvas Grid`;
          }
          return '';
        },
      },
    },
    name: 'Redraw Canvas Grid',
    icon: { iconName: 'redo' },
    description: `**Overview**<br/>
The Redraw Canvas Grid action is a UI utility component designed to force a visual refresh of a specific "Canvas Grid" component. While modern UI frameworks often handle reactivity automatically, complex grid layouts—especially those involving canvas rendering or dynamic resizing—may sometimes require an explicit trigger to recalculate their layout or repaint their content.<br/><br/>
This action functions as a manual "render" call. It ensures that the grid accurately reflects the current state of its underlying data or container dimensions, correcting potential visual artifacts or layout sync issues that can occur after significant state changes.

**Key Features**<br/>
* **Targeted Refresh:** Specifically operations on a single CanvasGrid component, allowing for precise control without affecting other parts of the page.
* **Strict Type Validation:** Implements validateItemId to ensure the provided targetItemId corresponds exactly to a CanvasGrid. If a developer attempts to target a different component type, the configuration is flagged as invalid.
* **Registry Filtering:** The property configuration uses initialFilter: 'CanvasGrid', streamlining the setup by restricting the dropdown list to show only valid grid targets.
* **Layout Correction:** Essential for scenarios where data updates or container resizing might not automatically trigger a full repaint of the canvas element.

**Properties**<br/>
* **Canvas Grid Item ID (targetItemId) (Required):** A property of type ItemID.
  * *Function:* Identifies the unique ID of the Canvas Grid component to be redrawn.
  * *Validation:* Must reference a valid component of type 'CanvasGrid'.

**Use Cases**<br/>
* **Dynamic Data Updates:** Triggering a redraw immediately after a complex set of data points has been calculated and pushed to the grid to ensure they render instantly.
* **Responsive Adjustments:** Calling this action after a parent container is resized or a side panel is toggled, ensuring the canvas grid recalculates its cell dimensions to fit the new space.
* **Visual Cleanup:** Using this action to clear visual artifacts that might persist after dragging and dropping elements within the grid.`,
  };
};

export default ActionRedrawCanvasGridManager;
